import React from "react";
import {
  Building2,
  CheckCircle2,
  ShieldCheck,
  Search,
  Wrench,
  Receipt,
  Scale,
  Calculator,
  ArrowRight,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LeadConsultationForm } from "@/components/LeadConsultationForm";

export interface ServiceDetailConfig {
  slug: string;
  badge: string;
  title: string;
  heroDesc: string;
  overview: string;
  benefits: string[];
  features: { title: string; desc: string }[];
  faqs: { question: string; answer: string }[];
  relatedServices: { title: string; link: string }[];
}

export const serviceConfigurations: Record<string, ServiceDetailConfig> = {
  "property-management": {
    slug: "property-management",
    badge: "Core Service Overview",
    title: "Full-Service Commercial & Residential Property Management",
    heroDesc:
      "Feingold Property Management provides comprehensive, customer-focused, and tech-driven management designed to protect your physical asset and optimize rental yields.",
    overview:
      "Managing real estate assets requires continuous oversight, legal knowledge, financial discipline, and proactive tenant relations. Feingold delivers a complete end-to-end operational framework for both residential single-family/multi-family properties and commercial developments. We align tenant satisfaction with owner profitability.",
    benefits: [
      "Comprehensive hands-free property operation and oversight",
      "Real-time online financial dashboards and transparent reporting",
      "Minimization of vacancy loss through tech-enabled tenant marketing",
      "Strict adherence to federal, state, and local fair housing laws",
    ],
    features: [
      {
        title: "Dedicated Asset Manager",
        desc: "A singular point of contact who knows your property and financial goals inside and out.",
      },
      {
        title: "24/7 Digital Portal Access",
        desc: "View monthly statements, maintenance logs, and tenant communication anytime.",
      },
      {
        title: "Regular Property Inspections",
        desc: "Routine preventative physical check-ups to catch minor issues before they become major costs.",
      },
    ],
    faqs: [
      {
        question:
          "How quickly can Feingold take over management of my property?",
        answer:
          "We can initiate onboarding within 24 to 48 hours. We handle all tenant notifications, lease transitions, and accounting setups seamlessly.",
      },
      {
        question:
          "Do you manage commercial real estate as well as residential?",
        answer:
          "Yes! Feingold specializes in both commercial complexes and residential properties, tailoring lease administration to your exact asset type.",
      },
    ],
    relatedServices: [
      { title: "Marketing & Leasing", link: "/marketing" },
      { title: "Tenant Screening", link: "/tenant-screening" },
      { title: "24/7 Maintenance", link: "/maintenance-services" },
    ],
  },
  marketing: {
    slug: "marketing",
    badge: "Tenant Acquisition",
    title: "Strategic Marketing & Leasing",
    heroDesc:
      "Attract high-caliber tenants quickly with professional digital syndication, high-resolution listings, and targeted marketing campaigns.",
    overview:
      "Every day a property sits vacant is revenue permanently lost. Feingold combines professional visual presentation with high-reach multi-channel digital syndication across top rental platforms to ensure maximum exposure and minimal turnaround times.",
    benefits: [
      "Multi-channel exposure across Zillow, Trulia, Realtor.com, MLS and social networks",
      "High-resolution visual presentations that highlight property strengths",
      "24/7 digital tour scheduling and lead tracking",
      "Competitive market pricing analysis to capture optimal rent",
    ],
    features: [
      {
        title: "Dynamic Rent Pricing",
        desc: "Data-backed market analysis ensuring your rent is set for maximum revenue without extended vacancy.",
      },
      {
        title: "Professional Listings",
        desc: "Crisp photography and compelling property descriptions that stand out in crowded feeds.",
      },
    ],
    faqs: [
      {
        question: "How long does it typically take to find a tenant?",
        answer:
          "Thanks to our rapid digital syndication, our average time to fill a vacant unit is approximately 14 days for qualified candidates.",
      },
    ],
    relatedServices: [
      { title: "Tenant Screening", link: "/tenant-screening" },
      { title: "Rent Collection", link: "/rent-collection" },
    ],
  },
  "tenant-screening": {
    slug: "tenant-screening",
    badge: "Risk Mitigation",
    title: "Rigorous Multi-Stage Tenant Screening",
    heroDesc:
      "Protect your property and rental cash flow with thorough credit, background, income, and eviction verifications.",
    overview:
      "Great property management begins with placing the right residents. Feingold executes a thorough 360-degree candidate evaluation process on every adult applicant to ensure financial responsibility and long-term lease compliance.",
    benefits: [
      "Nationwide criminal and eviction history background checks",
      "Comprehensive credit report analysis and debt ratio review",
      "Direct employer income verification and landlord reference audits",
      "Full compliance with Equal Housing and Fair Credit Reporting Acts",
    ],
    features: [
      {
        title: "Income Verification",
        desc: "We verify proof of income (pay stubs, tax records, bank statements) to ensure rent-to-income stability.",
      },
      {
        title: "Rental History Audit",
        desc: "Direct calls to former property managers to check past payment reliability and property care.",
      },
    ],
    faqs: [
      {
        question: "What criteria do you use to screen applicants?",
        answer:
          "We evaluate credit score thresholds, income-to-rent ratios (typically 3x rent), clean background checks, and positive prior landlord references.",
      },
    ],
    relatedServices: [
      { title: "Marketing & Leasing", link: "/marketing" },
      { title: "Eviction Coordination", link: "/eviction" },
    ],
  },
  "rent-collection": {
    slug: "rent-collection",
    badge: "Cash Flow Protection",
    title: "Automated Rent Collection & Disbursements",
    heroDesc:
      "Fast, frictionless digital payment systems for residents paired with reliable direct deposit disbursements for property owners.",
    overview:
      "Inconsistent rent collection disrupts your investment cash flow. Feingold provides residents with flexible 24/7 online payment options (ACH, credit, debit) with automatic late fee enforcement, giving owners predictable monthly disbursements.",
    benefits: [
      "Automated tenant payment reminders and recurring auto-pay options",
      "Direct electronic ACH deposit to owner bank accounts every month",
      "Enforced late payment policies and grace period compliance",
      "Real-time payment tracking on the owner digital portal",
    ],
    features: [
      {
        title: "Resident Portal",
        desc: "Easy digital app where tenants can set up automatic payments securely.",
      },
      {
        title: "Strict Policy Enforcement",
        desc: "Systematic notice delivery and late fee assessments if rent is ever delayed.",
      },
    ],
    faqs: [
      {
        question: "When do I receive my rental income each month?",
        answer:
          "Funds are disbursed electronically to owners following the monthly rent collection cut-off, accompanied by a detailed statement.",
      },
    ],
    relatedServices: [
      { title: "Accounting & Financials", link: "/accounting" },
      { title: "Property Management", link: "/property-management" },
    ],
  },
  "maintenance-services": {
    slug: "maintenance-services",
    badge: "Asset Preservation",
    title: "24/7 Maintenance & Vendor Coordination",
    heroDesc:
      "Protecting your physical structure with vetted, licensed, insured contractors and 24-hour emergency response.",
    overview:
      "Maintenance issues left unaddressed lead to tenant turnover and escalating repair costs. Feingold maintains a network of pre-screened, insured vendors who deliver quality work at preferred negotiated rates.",
    benefits: [
      "24/7 emergency repair dispatch hotline for residents",
      "Vetted, licensed, and insured contractor network",
      "No unapproved repair work above owner-designated limit",
      "Preventative seasonal physical property inspections",
    ],
    features: [
      {
        title: "Digital Work Orders",
        desc: "Track maintenance requests, contractor quotes, photos, and completed job invoices online.",
      },
      {
        title: "Preferred Vendor Pricing",
        desc: "Cost savings passed directly to property owners with zero hidden markups.",
      },
    ],
    faqs: [
      {
        question: "Are contractors licensed and insured?",
        answer:
          "Yes, every vendor in our network undergoes mandatory licensing, general liability, and worker compensation insurance verification.",
      },
    ],
    relatedServices: [
      { title: "Property Management", link: "/property-management" },
      { title: "Accounting & Financials", link: "/accounting" },
    ],
  },
  accounting: {
    slug: "accounting",
    badge: "Financial Intelligence",
    title: "Transparent Accounting & Financial Reporting",
    heroDesc:
      "Clear, itemized financial statements, real-time portal reporting, and annual tax preparation documents at your fingertips.",
    overview:
      "Keep track of every dollar generated by your real estate assets. Feingold provides institutional-grade accounting software that logs every income transaction, expense receipt, and disbursement with total clarity.",
    benefits: [
      "Detailed monthly Profit & Loss (P&L) statements",
      "On-demand digital ledger access through owner portal",
      "Simplified year-end 1099 processing and tax documents",
      "Organized digital document vault for all receipts and leases",
    ],
    features: [
      {
        title: "Monthly Statements",
        desc: "Easy-to-read financial reports sent directly to your inbox every month.",
      },
      {
        title: "Tax Ready Reporting",
        desc: "Streamlined annual summaries that make filing taxes with your CPA effortless.",
      },
    ],
    faqs: [
      {
        question: "Can my CPA get direct access to financial statements?",
        answer:
          "Yes, you can generate reports or download monthly/annual accounting packages directly from your owner portal.",
      },
    ],
    relatedServices: [
      { title: "Rent Collection", link: "/rent-collection" },
      { title: "Pricing & Plans", link: "/pricing" },
    ],
  },
  eviction: {
    slug: "eviction",
    badge: "Legal Resolution",
    title: "Eviction Coordination & Legal Compliance",
    heroDesc:
      "Professional lease enforcement, legal notice service, and attorney-guided eviction resolution when necessary.",
    overview:
      "While rigorous screening minimizes default, legal challenges can arise. Feingold navigates complex landlord-tenant regulations, ensuring strict compliance and swift legal resolution while protecting owner rights.",
    benefits: [
      "Strict lease terms enforcement and legal notice preparation",
      "Coordination with experienced real estate attorneys",
      "Representation and document preparation for court proceedings",
      "Swift unit turnover and re-leasing after possession recovery",
    ],
    features: [
      {
        title: "Compliant Notices",
        desc: "Proper service of statutory pay-or-quit notices according to local tenant law.",
      },
      {
        title: "Attorney Partnership",
        desc: "Direct collaboration with top eviction specialists to ensure fast resolution.",
      },
    ],
    faqs: [
      {
        question: "How common are evictions under Feingold management?",
        answer:
          "Evictions are extremely rare due to our rigorous multi-stage candidate screening process.",
      },
    ],
    relatedServices: [
      { title: "Tenant Screening", link: "/tenant-screening" },
      { title: "Property Management", link: "/property-management" },
    ],
  },
};

export const ServiceDetailPage: React.FC<{
  serviceKey: string;
  onOpenConsultation: () => void;
}> = ({ serviceKey, onOpenConsultation }) => {
  const config =
    serviceConfigurations[serviceKey] ||
    serviceConfigurations["property-management"];

  return (
    <div className="pt-24 pb-20 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-12 mb-16 shadow-2xl relative overflow-hidden border border-slate-800">
          <div className="relative z-10 max-w-3xl space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-accent bg-accent/20 px-3 py-1 rounded-md">
              {config.badge}
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight text-white">
              {config.title}
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              {config.heroDesc}
            </p>
            <div className="pt-4">
              <Button
                onClick={onOpenConsultation}
                size="lg"
                className="bg-accent text-slate-950 font-bold hover:bg-accent/90"
              >
                Get Service Consultation <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Overview & Benefits */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7 space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
              Service Overview
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
              {config.overview}
            </p>

            <h3 className="text-xl font-bold text-slate-900 dark:text-white pt-4">
              Key Owner Benefits
            </h3>
            <div className="space-y-3">
              {config.benefits.map((b, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800 shadow-sm"
                >
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {b}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-lg">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6">
                Highlighted Features
              </h3>
              <div className="space-y-6">
                {config.features.map((f, i) => (
                  <div key={i} className="space-y-1">
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-primary" />{" "}
                      {f.title}
                    </h4>
                    <p className="text-xs text-slate-500 leading-relaxed pl-4">
                      {f.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* FAQs */}
        {config.faqs.length > 0 && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 border border-slate-200/80 dark:border-slate-800 mb-16 shadow-sm">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" /> Frequently Asked
              Questions
            </h3>
            <div className="space-y-6">
              {config.faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="border-b border-slate-100 dark:border-slate-800 pb-4 last:border-0 last:pb-0"
                >
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-1">
                    {faq.question}
                  </h4>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Consultation Form */}
        <div className="max-w-4xl mx-auto">
          <LeadConsultationForm title={`Inquire About ${config.title}`} />
        </div>
      </div>
    </div>
  );
};
